import React, { useState, useEffect } from 'react';
import subscriptionService from '../../utils/services/subscriptionService';
import Toast from '../Toast/Toast';
import logoIcon from '../../assets/logo_icon.png';

function SubscriptionPlansModal({ isOpen, onClose }) {
    const [plans, setPlans] = useState([]);
    const [userSubscriptions, setUserSubscriptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSport, setSelectedSport] = useState('NBA');
    const [processingPlanKey, setProcessingPlanKey] = useState(null);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen]);

    const fetchData = async () => {
        setIsLoading(true);
        setError(null);
        
        // Fetch both plans and user subscriptions
        const [plansResult, subscriptionsResult] = await Promise.all([
            subscriptionService.getAllPlans(),
            subscriptionService.getUserSubscriptions()
        ]);
        
        if (plansResult.success) {
            setPlans(plansResult.data);
        } else {
            setError(plansResult.message);
        }
        
        if (subscriptionsResult.success) {
            setUserSubscriptions(subscriptionsResult.data);
        }
        
        setIsLoading(false);
    };

    const getSubscriptionLogo = () => logoIcon;

    // Define tier hierarchy (higher number = higher tier)
    const getTierLevel = (tier) => {
        const tierLevels = {
            'BASIC': 1,
            'PRO': 2,
            'ELITE': 3,
            'EV_FOUNDERS': 4,
            'LIFETIME': 5,
        };
        return tierLevels[tier] || 0;
    };

    // Get user's current tier for a specific sport
    // Includes active, trialing, AND past_due subscriptions
    const getUserCurrentTier = (sport) => {
        // Find any active, trialing, or past_due subscription for this sport
        const currentSub = userSubscriptions.find(sub => 
            sub.sport === sport && 
            (sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due')
        );
        return currentSub ? currentSub.tier : null;
    };

    // Determine if plan is upgrade, downgrade, or new
    const getPlanAction = (plan, sport) => {
        const currentTier = getUserCurrentTier(sport);
        if (!currentTier) return 'subscribe'; // User has no plan for this sport
        
        const currentLevel = getTierLevel(currentTier);
        const planLevel = getTierLevel(plan.tier);
        
        if (planLevel > currentLevel) return 'upgrade';
        if (planLevel < currentLevel) return 'downgrade';
        return 'current'; // Same tier (should be marked as subscribed)
    };

    const handleSubscribe = async (plan) => {
        // Prevent multiple clicks
        if (processingPlanKey) return;

        setProcessingPlanKey(plan.key);

        try {
            const result = await subscriptionService.createCheckoutSession(plan.key);

            if (result.success && result.data?.url) {
                // Redirect to Stripe Checkout
                window.location.href = result.data.url;
            } else {
                // Show error toast
                setToast({
                    type: 'error',
                    message: result.message || 'Failed to create checkout session'
                });
                setProcessingPlanKey(null);
            }
        } catch (err) {
            setToast({
                type: 'error',
                message: 'An unexpected error occurred. Please try again.'
            });
            setProcessingPlanKey(null);
        }
    };

    const closeToast = () => {
        setToast(null);
    };

    // Helper function to check if user has any subscription for a sport
    // Includes active, trialing, AND past_due subscriptions
    const userHasSubscriptionForSport = (sport) => {
        // Check if user has any active, trialing, or past_due subscription for this sport
        return userSubscriptions.some(sub => 
            sub.sport === sport && 
            (sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due')
        );
    };

    // Get unique sports for filter in the order: NBA, AFL, ALL
    // Filter out sports where user already has a subscription
    const uniqueSports = [...new Set(plans.map(p => p.sport))].filter(sport => !userHasSubscriptionForSport(sport));
    const sports = [];
    
    // Add in specific order
    if (uniqueSports.includes('NBA')) sports.push('NBA');
    if (uniqueSports.includes('AFL')) sports.push('AFL');
    if (uniqueSports.includes('ALL')) sports.push('ALL');
    
    // Add any other sports that might exist
    uniqueSports.forEach(sport => {
        if (!['NBA', 'AFL', 'ALL'].includes(sport)) {
            sports.push(sport);
        }
    });

    // Update selected sport if current selection is no longer available
    useEffect(() => {
        if (isOpen && !isLoading && sports.length > 0 && !sports.includes(selectedSport)) {
            setSelectedSport(sports[0]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isLoading, selectedSport, sports.length, userSubscriptions.length]);

    // Filter plans based on selected sport
    const filteredPlans = plans.filter(p => p.sport === selectedSport);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
                <div className="relative bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl sm:rounded-2xl shadow-2xl max-w-6xl w-full border border-slate-800/50 max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
                    {/* Header */}
                    <div className="sticky top-0 z-10 bg-gradient-to-br from-[#0f172a] to-[#020617] border-b border-slate-800/50 px-4 sm:px-6 py-3 sm:py-4">
                        <div className="flex items-center justify-between mb-3 sm:mb-4">
                            <div className="flex-1 text-center pr-8">
                                <h2 className="text-lg sm:text-2xl font-bold text-white">Choose Your Plan</h2>
                                <p className="text-slate-400 text-xs sm:text-sm mt-1">Select a subscription plan that works for you</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-slate-400 hover:text-white transition-colors duration-200 absolute right-4 sm:right-6 top-3 sm:top-4"
                            >
                                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Sport Filter */}
                        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            {sports.map((sport) => (
                                <button
                                    key={sport}
                                    onClick={() => setSelectedSport(sport)}
                                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 ${
                                        selectedSport === sport
                                            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/30'
                                            : 'bg-slate-800/30 text-slate-400 hover:bg-slate-800/50 hover:text-white'
                                    }`}
                                >
                                    {sport}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(95vh-160px)] sm:max-h-[calc(90vh-180px)]">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
                                    <p className="mt-4 text-slate-400">Loading plans...</p>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="text-center py-12">
                                <div className="text-red-400 mb-4">
                                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <p className="text-lg font-semibold text-white mb-2">Error loading plans</p>
                                <p className="text-slate-400 mb-4">{error}</p>
                                <button
                                    onClick={fetchData}
                                    className="bg-emerald-600 hover:bg-emerald-700 px-6 py-2 rounded-lg font-semibold transition duration-200"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : sports.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <p className="text-xl font-semibold text-white mb-2">All Set!</p>
                                <p className="text-slate-400 mb-4">You're subscribed to all available sports. Use the "Manage Subscription" button to upgrade, downgrade, or modify your existing plans.</p>
                                <button
                                    onClick={onClose}
                                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 px-6 py-3 rounded-lg font-semibold transition duration-200"
                                >
                                    Close
                                </button>
                            </div>
                        ) : filteredPlans.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-slate-400 text-lg">No plans available for {selectedSport}</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {filteredPlans.map((sportCategory) => (
                                    <div key={sportCategory.sport}>
                                        {/* Plans Grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                            {sportCategory.plans
                                                .filter(plan => plan.show !== false) // Only show plans where show is true or undefined
                                                .map((plan) => {
                                                // Check if user is subscribed to this exact plan (active, trialing, or past_due)
                                                const isSubscribed = userSubscriptions.some(sub => 
                                                    sub.key === plan.key && 
                                                    (sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due')
                                                );
                                                const isSoldOut = !plan.is_available;
                                                const hasTrial = plan.trial_days > 0;
                                                const hasLimitedSpots = !plan.unlimited && plan.max_spots && plan.spots_remaining > 0;
                                                const planAction = getPlanAction(plan, sportCategory.sport);
                                                const canPurchase = plan.can_purchase !== false; // Default to true if not specified

                                                return (
                                                    <div
                                                        key={plan.key}
                                                        className="relative bg-gradient-to-br from-[#0f172a]/50 to-[#020617]/50 rounded-lg sm:rounded-xl p-4 sm:p-5 border border-slate-800/50 hover:border-emerald-500/30 transition-all duration-300 group flex flex-col"
                                                    >
                                                        {/* Top Badges */}
                                                        <div className="flex items-center gap-2 mb-3 flex-wrap min-h-[28px]">
                                                            {isSubscribed && (
                                                                <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                    ✓ Subscribed
                                                                </span>
                                                            )}
                                                            {isSoldOut && (
                                                                <span className="bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                    Sold Out
                                                                </span>
                                                            )}
                                                            {!isSoldOut && hasTrial && !isSubscribed && (
                                                                <span className="bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                    {plan.trial_days}-Day Trial
                                                                </span>
                                                            )}
                                                            {!isSoldOut && hasLimitedSpots && (
                                                                <span className="bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                    {plan.spots_remaining} spots left
                                                                </span>
                                                            )}
                                                            {!isSoldOut && plan.unlimited && (
                                                                <span className="bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                    Unlimited spots
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Logo and Plan Title */}
                                                        <div className="mb-3 text-center">
                                                            <div className="w-14 h-14 rounded-lg flex items-center justify-center -mb-1 mx-auto">
                                                                <img 
                                                                    src={getSubscriptionLogo()} 
                                                                    alt="Snipe"
                                                                    className="w-full h-full object-contain"
                                                                />
                                                            </div>
                                                            <h4 className="text-lg font-bold text-white">
                                                                {plan.display_name || `${sportCategory.sport} ${plan.tier}`}
                                                            </h4>
                                                            <div className="flex items-center justify-center gap-1.5 mt-1.5">
                                                                {(plan.tier === 'BASIC' ? ['NBA'] : ['NBA', 'AFL']).map(sp => (
                                                                    <span key={sp} className="inline-flex items-center bg-white/[0.04] border border-white/[0.08] px-2 py-0.5 rounded text-[11px] font-medium text-slate-300">
                                                                        {sp}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Price */}
                                                        <div className="mb-3">
                                                            {plan.monthly_price ? (
                                                                <div className="flex items-baseline justify-center">
                                                                    <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                                                                        ${parseFloat(plan.monthly_price).toFixed(2)}
                                                                    </span>
                                                                    <span className="text-slate-400 text-sm ml-2">/month</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                                                                    Lifetime Access
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Features */}
                                                        <div className="space-y-2 mb-4 flex-grow">
                                                            {plan.subscription_features && plan.subscription_features.length > 0 ? (
                                                                plan.subscription_features.map((feature, featureIndex) => (
                                                                    <div key={featureIndex} className="flex items-center text-sm text-slate-400">
                                                                        <svg className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                        {feature}
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <>
                                                                    {plan.unlimited === true && (
                                                                        <div className="flex items-center text-sm text-slate-400">
                                                                            <svg className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                            Unlimited capacity
                                                                        </div>
                                                                    )}
                                                                    {!plan.unlimited && plan.max_spots !== null && plan.max_spots !== undefined && (
                                                                        <div className="flex items-center text-sm text-slate-400">
                                                                            <svg className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                            Limited to {plan.max_spots} members
                                                                        </div>
                                                                    )}
                                                                    {!isSoldOut && hasTrial && (
                                                                        <div className="flex items-center text-sm text-slate-400">
                                                                            <svg className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                            {plan.trial_days} days free trial
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>

                                                        {/* Subscribe Button */}
                                                        <button
                                                            onClick={() => handleSubscribe(plan)}
                                                            disabled={isSubscribed || isSoldOut || !canPurchase || processingPlanKey !== null}
                                                            className={`w-full py-2.5 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                                                                isSubscribed || isSoldOut || !canPurchase || processingPlanKey !== null
                                                                    ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                                                                    : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 hover:scale-[1.02] transform'
                                                            }`}
                                                        >
                                                            {processingPlanKey === plan.key ? (
                                                                <>
                                                                    <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-transparent"></div>
                                                                    Processing...
                                                                </>
                                                            ) : isSubscribed ? (
                                                                <>
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                    Already Subscribed
                                                                </>
                                                            ) : !canPurchase ? (
                                                                <>
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                    </svg>
                                                                    Coming Soon
                                                                </>
                                                            ) : isSoldOut ? (
                                                                <>
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                                    </svg>
                                                                    Sold Out
                                                                </>
                                                            ) : planAction === 'upgrade' ? (
                                                                <>
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                                    </svg>
                                                                    Upgrade Plan
                                                                </>
                                                            ) : planAction === 'downgrade' ? (
                                                                <>
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                                                    </svg>
                                                                    Downgrade Plan
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                                                    </svg>
                                                                    {hasTrial ? 'Purchase' : 'Purchase'}
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Toast Notification */}
            {toast && (
                <Toast 
                    type={toast.type}
                    message={toast.message}
                    onClose={closeToast}
                />
            )}
        </div>
    );
}

export default SubscriptionPlansModal;

